import { PdfIngredientRow } from './build-pdf-ingredient-rows';

/** One table of processing, packaging or transport types. */
export interface PptSection {
  stage: 'processing' | 'packaging' | 'transport';
  title: string;
  rowLabel: string;
  shareOfTotal: number;
  rows: Array<{ name: string; emissions: number }>;
}

export interface BreakdownPage {
  /** Ingredient rows on this page; empty when the page only holds PPT. */
  ingredientRows: PdfIngredientRow[];
  /** True on the page that ends the ingredient table. */
  showTotal: boolean;
  sections: PptSection[];
}

// Rows are a fixed height, so each page has a deterministic row budget. The
// first page carries the title block and the legend, so it holds fewer rows.
// These budgets assume the h-7 (28px) rows and h-8 total row in
// product-pdf-breakdown-pages.tsx and product-pdf-ppt-sections.tsx, and the
// header and footer in product-pdf-page.tsx: change those and re-check these.
export const ROWS_ON_FIRST_PAGE = 23;
export const ROWS_PER_PAGE = 29;
const TOTAL_ROW_COST = 2;
// Section title, table header and the gap above it.
const SECTION_OVERHEAD = 3;

const sectionCost = (section: PptSection) =>
  section.rows.length + SECTION_OVERHEAD;

/**
 * Flows the ingredient table, its total row and the PPT sections onto pages.
 * The PPT sections share the final ingredient page when all of them fit in
 * the space left; otherwise they start a fresh page and flow from there.
 */
export function buildBreakdownPages(
  rows: readonly PdfIngredientRow[],
  sections: readonly PptSection[]
): BreakdownPage[] {
  const pages: BreakdownPage[] = [];
  let remaining = [...rows];
  let budget = ROWS_ON_FIRST_PAGE;
  do {
    pages.push({
      ingredientRows: remaining.slice(0, budget),
      showTotal: false,
      sections: [],
    });
    remaining = remaining.slice(budget);
    budget = ROWS_PER_PAGE;
  } while (remaining.length > 0);

  const last = pages[pages.length - 1];
  last.showTotal = true;
  let used = last.ingredientRows.length + TOTAL_ROW_COST;
  const pageBudget = pages.length === 1 ? ROWS_ON_FIRST_PAGE : ROWS_PER_PAGE;

  const allSectionsCost = sections.reduce((sum, s) => sum + sectionCost(s), 0);
  let current = last;
  if (sections.length && used + allSectionsCost > pageBudget) {
    current = { ingredientRows: [], showTotal: false, sections: [] };
    pages.push(current);
    used = 0;
  }
  for (const section of sections) {
    if (
      current.sections.length &&
      used + sectionCost(section) > ROWS_PER_PAGE
    ) {
      current = { ingredientRows: [], showTotal: false, sections: [] };
      pages.push(current);
      used = 0;
    }
    current.sections.push(section);
    used += sectionCost(section);
  }
  return pages;
}
